import {
  PaintBlockFiltersInput,
  useDefectsAreaForCustomBlocksQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import * as localState from '../state';
import { useCallback, useMemo } from 'react';
import { useResponsiveHeight } from '@/hooks/useResponsiveHeight';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { CustomBlockListItem } from './CustomBlockListItem';
import { CustomBlockListSearch } from './CustomBlockListSearch';
import { CustomBlockSortingHeader } from './CustomBlockSortingHeader';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { PageLoader } from '@/components/shared/PageLoader';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { CustomBlockFilterChips } from './CustomBlockFilterChips';
import { BlockListError } from './BlockListError';

const LIMIT_SIZE = 50;

export const PaintBlocksList = () => {
  const structureId = useGetStructureId();
  const selectedPaintRegionGroupId = useAtomValue(state.selectedPaintRegionGroupId);
  const searchTerm = useAtomValue(localState.customBlockSearchTermDebounced.debouncedValueAtom);

  const [height, boxRef] = useResponsiveHeight();

  const [customBlocksQueryInput, setCustomBlocksQueryInput] = useAtom(
    localState.customBlocksQueryInput
  );
  const customBlockQueryFilters = useAtomValue(localState.customBlockQueryFilters);
  const setAllCustomBlocks = useSetAtom(localState.allCustomBlocks);

  const queryInput: PaintBlockFiltersInput = useMemo(() => {
    return {
      structureIds: [structureId],
      paintRegionGroupIds: selectedPaintRegionGroupId ? [selectedPaintRegionGroupId] : undefined,
      ...customBlockQueryFilters,
      search: searchTerm,
    };
  }, [structureId, selectedPaintRegionGroupId, customBlockQueryFilters, searchTerm]);

  const { data, loading, error, refetch } = useDefectsAreaForCustomBlocksQuery({
    variables: {
      input: queryInput,
      pageInfo: {
        skip: 0,
        limit: LIMIT_SIZE,
        sortBy: customBlocksQueryInput.sortBy,
        sortDir: customBlocksQueryInput.sortDir,
      },
    },
    fetchPolicy: 'cache-first',
    skip: !selectedPaintRegionGroupId || !structureId,
    onCompleted: (response) => {
      const blocks = response?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock;
      setAllCustomBlocks(blocks);
    },
  });

  const loadMoreItems = useCallback(async () => {
    if (loading || !customBlocksQueryInput.hasMoreItems) return;

    try {
      const result = await refetch({
        input: queryInput,
        pageInfo: {
          skip: 0,
          limit: LIMIT_SIZE * customBlocksQueryInput.page,
          sortBy: customBlocksQueryInput.sortBy,
          sortDir: customBlocksQueryInput.sortDir,
        },
      });

      const newItems =
        result.data?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock || [];

      if (newItems.length < LIMIT_SIZE * customBlocksQueryInput.page) {
        setCustomBlocksQueryInput((prev) => ({
          ...prev,
          hasMoreItems: false,
        }));
      }

      setCustomBlocksQueryInput((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    } catch (err) {
      const message = extractErrorMessageFromUnknownError(err);
      console.error('Error loading more custom blocks', message);
    }
  }, [
    loading,
    customBlocksQueryInput.hasMoreItems,
    customBlocksQueryInput.page,
    customBlocksQueryInput.sortBy,
    customBlocksQueryInput.sortDir,
    refetch,
    queryInput,
    setCustomBlocksQueryInput,
  ]);

  const blocks = useMemo(() => {
    return data?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock || [];
  }, [data?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock]);

  const isItemLoaded = (index: number) =>
    !customBlocksQueryInput.hasMoreItems || index < blocks.length;
  const itemCount = customBlocksQueryInput.hasMoreItems ? blocks.length + 1 : blocks.length;

  if (error) {
    return <BlockListError message={error.message} />;
  }

  const noBlocksFound = blocks.length === 0 && !loading;

  return (
    <Stack height="100%">
      <CustomBlockFilterChips BoxProps={{ m: 1 }} />
      <CustomBlockListSearch
        sx={{ px: 1, my: 2 }}
        defects={data?.defectsAreaForAssemblyPaintRegionsByGroup}
        loading={loading}
      />
      <CustomBlockSortingHeader />

      <Box hidden={!loading} sx={{ position: 'relative', top: '58px' }}>
        <PageLoader />
      </Box>

      <Box sx={{ flexGrow: 1 }} hidden={loading} ref={boxRef}>
        {noBlocksFound ? (
          <Box p={2}>
            <Typography variant="h5" color="info" textAlign="center" mt={5}>
              No blocks found
            </Typography>
          </Box>
        ) : (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered, ref }) => (
              <List
                height={height || 500}
                itemCount={itemCount}
                itemSize={50}
                onItemsRendered={onItemsRendered}
                ref={ref}
                width="100%"
              >
                {({ index, style }) => {
                  if (!isItemLoaded(index)) {
                    return (
                      <Box style={style} display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress />
                      </Box>
                    );
                  }
                  const item = blocks[index];
                  return (
                    <div style={{ ...style, padding: 0 }}>
                      {item && <CustomBlockListItem key={item.paintRegionId} block={item} />}
                    </div>
                  );
                }}
              </List>
            )}
          </InfiniteLoader>
        )}
      </Box>
    </Stack>
  );
};
